import { useCallback, useEffect, useRef, useState } from 'react';
import { type QnAStateType } from '@uber/react-qna-lib';
import { useAtom, useAtomValue } from 'jotai';
import { convertAnswersToUserAnswerMap } from '../../../common/side-popover-panel/utils';
import type { AnswerT } from '../../../common/types/types';
import type { CachedQuestionnaireResponse } from '../atoms/questionnaire.atom';
import { questionnaireDetailsMapAtom } from '../atoms/questionnaire.atom';
type UseFetchQuestionnaireProps = {
  fetchQuestionnaire?: (questionnaireId: string) => Promise<QnAStateType>;
};
type FetchQuestionnaireFunction = (questionnaireId: string, forceRefetch?: boolean) => Promise<CachedQuestionnaireResponse | undefined>;
export const useFetchQuestionnaire = ({
  fetchQuestionnaire
}: UseFetchQuestionnaireProps): FetchQuestionnaireFunction => {
  const [questionnaireDetailsMap, setQuestionnaireDetailsMap] = useAtom(questionnaireDetailsMapAtom);
  const fetchAndCache = useCallback(async (questionnaireId: string, forceRefetch = false): Promise<CachedQuestionnaireResponse | undefined> => {
    if (!questionnaireId) {
      return undefined;
    }
    const cachedData = questionnaireDetailsMap[questionnaireId];
    if (cachedData && !forceRefetch) {
      return cachedData.status !== 'loading' ? cachedData : undefined;
    }
    if (!fetchQuestionnaire) {
      return undefined;
    }
    try {
      setQuestionnaireDetailsMap(prev => ({
        ...prev,
        [questionnaireId]: {
          status: 'loading'
        }
      }));
      const fetchedData = await fetchQuestionnaire(questionnaireId);
      setQuestionnaireDetailsMap(prev => ({
        ...prev,
        [questionnaireId]: {
          status: 'success',
          questionnaire: fetchedData
        }
      }));
      return {
        status: 'success',
        questionnaire: fetchedData
      };
    } catch (error) {
      setQuestionnaireDetailsMap(prev => ({
        ...prev,
        [questionnaireId]: {
          status: 'error',
          error: (error as Error)
        }
      }));
      return {
        status: 'error',
        error: (error as Error)
      };
    }
  }, [questionnaireDetailsMap, fetchQuestionnaire, setQuestionnaireDetailsMap]);
  return fetchAndCache;
};
type UseQuestionnaireProps = {
  fetchQuestionnaire?: (questionnaireId: string) => Promise<QnAStateType>;
  answers?: AnswerT;
  questionnaireId: string;
};
type UseQuestionnaireReturn = {
  questionnaire: QnAStateType | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
};
export const useQuestionnaire = ({
  fetchQuestionnaire,
  answers,
  questionnaireId
}: UseQuestionnaireProps): UseQuestionnaireReturn => {
  const fetchAndCache = useFetchQuestionnaire({
    fetchQuestionnaire
  });
  const cachedQuestionnaireMap = useAtomValue(questionnaireDetailsMapAtom);
  const cachedQuestionnaire = cachedQuestionnaireMap[questionnaireId];
  const [questionnaire, setQuestionnaire] = useState<QnAStateType | null>(null);

  // To prevent race conditions, we keep track of the ID of the request in flight.
  const inFlightRequestId = useRef<string | null>(null);
  const processQuestionnaire = useCallback((questionnaireData: QnAStateType) => {
    // Create a copy to avoid mutating the cached version
    let processedQuestionnaire = {
      ...questionnaireData,
      answers: {}
    };

    // Merge with answers if provided
    if (answers && Object.keys(answers).length > 0) {
      processedQuestionnaire = {
        ...processedQuestionnaire,
        answers: convertAnswersToUserAnswerMap(processedQuestionnaire, answers)
      };
    }
    setQuestionnaire(processedQuestionnaire);
  }, [answers]);
  const loadQuestionnaire = useCallback(async (forceRefetch = false) => {
    const requestId = questionnaireId;
    inFlightRequestId.current = requestId;
    if (!requestId) {
      setQuestionnaire(null);
      return;
    }
    const fetchedData = await fetchAndCache(requestId, forceRefetch);
    if (inFlightRequestId.current !== requestId) {
      return;
    }
    if (!fetchedData) {
      setQuestionnaire(null);
      return;
    }
    switch (fetchedData.status) {
      case 'success':
        {
          processQuestionnaire(fetchedData.questionnaire);
          break;
        }
      case 'error':
        {
          setQuestionnaire(null);
          break;
        }
    }
  }, [questionnaireId, fetchAndCache, processQuestionnaire]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadQuestionnaire();
  }, [loadQuestionnaire]);
  const retry = useCallback(() => {
    loadQuestionnaire(true);
  }, [loadQuestionnaire]);
  const hasFetchedQuestionnaire = Boolean(fetchQuestionnaire) && Boolean(questionnaireId);
  return {
    questionnaire,
    isLoading: cachedQuestionnaire ? cachedQuestionnaire?.status === 'loading' : hasFetchedQuestionnaire,
    error: cachedQuestionnaire?.status === 'error' ? cachedQuestionnaire.error?.message : null,
    retry
  };
};